import type { EnvironmentsAttributeMetadata } from '@customerio/hydra-ui-api';
import { PERMANENT_ATTRIBUTES, PERMANENT_OBJECT_ATTRIBUTES, RESTRICTED_ATTRIBUTES } from '@/utils/attributes';

/**
 * Descriptions for the attributes and events Customer.io owns.
 *
 * A workspace has written nothing about these to begin with, so the copy
 * lives here rather than in its metadata — as a default, not a ceiling: the
 * drawer and the item page both let an attribute's description be written,
 * and `resolveAttributeDescription` prefers the stored one from then on. A
 * reserved *event* is the exception, where this copy keeps winning. It
 * follows the public documentation at
 * https://docs.customer.io/journeys/attributes/#reserved-attributes, and the
 * person/campaign nouns come from the workspace's taxonomy so a workspace that
 * calls people "subscribers" reads consistently.
 */

/** The taxonomy terms the reserved copy is written around. */
export type ReservedDescriptionTerms = {
  campaignsPlural: string;
  personSingular: string;
  personPlural: string;
  customerSingular: string;
};

export const DEFAULT_RESERVED_DESCRIPTION_TERMS: ReservedDescriptionTerms = {
  campaignsPlural: 'campaigns',
  personSingular: 'person',
  personPlural: 'people',
  customerSingular: 'customer',
};

function reservedAttributeDescriptions({
  campaignsPlural,
  personSingular,
  personPlural,
  customerSingular,
}: ReservedDescriptionTerms) {
  return new Map<string, string>(
    Object.entries({
      id: `A unique identifier for ${personPlural}. If the id does not yet exist, we create a new ${personSingular}.`,
      email: `A ${personSingular}'s email address. If your workspace uses email as a unique identifier (the default setting for new workspaces), and the email address does not yet exist, we create a new ${personSingular}. The TO line of your email templates is prefilled with this attribute.`,
      cio_id: `A unique, immutable identifier set by Customer.io, set automatically when you add a ${personSingular}.`,

      _created_in_customerio_at: `The date-time when a ${personSingular} was added in Customer.io. This value can be different from created_at. In the API, this value is represented by the timestamp for the cio_id attribute.`,

      unsubscribed: `Determines whether a ${personSingular} is subscribed or unsubscribed from your ${campaignsPlural} and newsletters. We support any case of true (i.e. TRUE, true, tRUe, etc.), 1, or "1" to represent unsubscribed. Any other value is considered "false", or subscribed.`,

      mobile_ad_id:
        "Used to record either Apple's Advertising Identifier (IDFA) or Android's Advertising ID (AAID). This id can improve match accuracy in Google or Facebook Ads when using Ad Audiences.",
      email_sha256: `To keep your data secure, you can hash your ${customerSingular} email using the SHA256 algorithm before sending it to your account. This field is only used in Ad Audiences and cannot be used to send messages.`,
      mobile_ad_id_sha256:
        'To keep your data secure, you can hash the mobile_ad_id using the SHA256 algorithm before sending it to your account. This field is only used in Ad Audiences.',

      timezone: "The user's timezone. Used for sending localized messages.",

      anonymous_id: `Used for anonymous tracking before a ${personSingular} is identified.`,

      cio_test_user:
        'Set to true on profiles designated as test-user recipients for the Builder plan. Set by Customer.io via the profile Options menu; not editable directly.',
      cio_double_opt_in:
        'Set to true by Customer.io once a test-user recipient confirms their email via the verification link. Not editable directly.',
    }),
  );
}

const RESERVED_OBJECT_ATTRIBUTE_DESCRIPTIONS = new Map<string, string>(
  Object.entries({
    _created_in_customerio_at:
      'The date-time when the object was first created in Customer.io. This value can be different from created_at.',
    name: 'The name of the object. This is a required field when creating an object.',
    cio_object_id:
      'A unique, immutable identifier for objects provided by Customer.io. If this does not yet exist in your workspace, we create a new object.',
    object_id: 'A unique identifier for objects. If the object_id does not yet exist, we create a new object.',
    objectId: 'An analog for object_id in some Customer.io integrations.',
    created_at: 'Unix timestamp when the object was first created. Used when listing objects in the UI, for example.',
  }),
);

const RESERVED_EVENT_DESCRIPTIONS = new Map<string, string>(
  Object.entries({
    form_submit: 'Captures form submissions from your website or app.',
    from_address: 'The email address of the sender.',
    reply_to: 'The email address of the reply-to recipient.',
    recipient: 'The email address of the recipient.',
  }),
);

/** The reserved copy for a person attribute, or null when it isn't reserved. */
export function getReservedAttributeDescription(attributeId: string, terms: ReservedDescriptionTerms): string | null {
  return reservedAttributeDescriptions(terms).get(attributeId) ?? null;
}

/** The reserved copy for an object attribute, or null when it isn't reserved. */
export function getReservedObjectAttributeDescription(attributeId: string): string | null {
  return RESERVED_OBJECT_ATTRIBUTE_DESCRIPTIONS.get(attributeId) ?? null;
}

/** The reserved copy for an event, or null when it isn't reserved. */
export function getReservedEventDescription(eventId: string): string | null {
  return RESERVED_EVENT_DESCRIPTIONS.get(eventId) ?? null;
}

export function isReservedProfileAttribute(attributeId: string): boolean {
  return RESTRICTED_ATTRIBUTES.includes(attributeId) || PERMANENT_ATTRIBUTES.includes(attributeId);
}

export function isReservedObjectAttribute(attributeId: string): boolean {
  return PERMANENT_OBJECT_ATTRIBUTES.includes(attributeId);
}

export function isReservedEvent(eventId: string): boolean {
  return RESERVED_EVENT_DESCRIPTIONS.has(eventId);
}

/** The metadata fields a description is resolved from. */
export type DescribableMetadata = Pick<EnvironmentsAttributeMetadata, 'description' | 'generated_description'>;

/**
 * The description to show for an attribute: what someone wrote, else the
 * reserved copy for a name Customer.io owns, else what the model generated.
 *
 * Relationship attributes have no reserved copy of their own, so they skip
 * straight from custom to generated.
 */
export function resolveAttributeDescription(
  attributeId: string,
  kind: 'attribute' | 'object-attribute' | 'object-relationship-attribute',
  metadata: DescribableMetadata | null | undefined,
  terms: ReservedDescriptionTerms,
): string | null {
  if (metadata == null) {
    return null;
  }
  if (metadata.description != null && metadata.description !== '') {
    return metadata.description;
  }

  const reserved =
    kind === 'object-attribute' && isReservedObjectAttribute(attributeId)
      ? getReservedObjectAttributeDescription(attributeId)
      : kind === 'attribute' && isReservedProfileAttribute(attributeId)
        ? getReservedAttributeDescription(attributeId, terms)
        : null;

  return reserved ?? metadata.generated_description ?? null;
}

/**
 * The description to show for an event. Reserved events always show their
 * reserved copy — their descriptions are not editable — so it wins over
 * anything stored.
 */
export function resolveEventDescription(eventId: string, metadata: DescribableMetadata | null | undefined) {
  if (isReservedEvent(eventId)) {
    return getReservedEventDescription(eventId);
  }
  if (metadata == null) {
    return null;
  }
  return metadata.description === null ? (metadata.generated_description ?? null) : metadata.description;
}
